import {
  normalizePositionConfigurations,
  Position,
  WindowAddress,
  WindowOrder,
  WindowOrderSummary,
} from '@cutr/constants/windows/windows';
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, useRevalidator } from 'react-router-dom';

import { PaginatedData } from '@/api/backend/agent';
import { hashPosition, usePositionStore } from '@/windows/api/positionStore';
import { hashOrder, useWindowStore } from '@/windows/api/windowStore';
import { windowApi } from '@/windows/queries/windowApi';

import { keys as positionKeys } from '../position/keys';
import { keys } from './keys';

export const syncPositionCaches = (
  queryClient: QueryClient,
  order: Pick<WindowOrder, 'id' | 'positions' | 'configurations'>
): Position[] => {
  const keptFromCache = new Set<Position['id']>();
  const positions = order.positions.map((position) => {
    const cached = queryClient.getQueryData<Position>(
      positionKeys.getOne(position.id)
    );
    if (
      !cached ||
      Date.parse(cached.updatedAt) <= Date.parse(position.updatedAt)
    ) {
      return position;
    }
    keptFromCache.add(position.id);
    return normalizePositionConfigurations(cached, order.configurations);
  });
  queryClient.setQueryData<Position[]>(
    positionKeys.getAll(order.id),
    positions
  );
  for (const position of positions) {
    queryClient.setQueryData<Position>(
      positionKeys.getOne(position.id),
      position
    );
  }

  const positionStore = usePositionStore.getState();
  const openPosition = positions.find(({ id }) => id === positionStore.id);

  if (!openPosition) return positions;

  if (
    keptFromCache.has(openPosition.id) ||
    hashPosition(positionStore) !== positionStore.hash
  ) {
    const { configurations, windowSpec } = normalizePositionConfigurations(
      positionStore,
      order.configurations
    );
    usePositionStore.setState({ configurations, windowSpec });
    return positions;
  }

  if (hashPosition(openPosition) !== positionStore.hash) {
    positionStore.init(openPosition);
  }
  return positions;
};

export const useCreateOrder = (title?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const newWindowTitle = t('reseller.windowTitle');

  return useMutation({
    mutationFn: () => windowApi.createOrder(title || newWindowTitle),
    mutationKey: keys.create,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.getAllPrefix });
    },
  });
};

export const getOrderQuery = (orderId: string) => ({
  queryKey: keys.getOne(orderId),
  queryFn: () => windowApi.getOrder(orderId),
});

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  const { setHash } = useWindowStore();
  const revalidator = useRevalidator();
  const { orderId } = useParams();

  if (!orderId) {
    throw new Error('orderId must be in the URL to update an order');
  }

  return useMutation({
    mutationFn: ({ data }: { data: Partial<WindowOrder> }) =>
      windowApi.patchOrder(orderId, data),
    mutationKey: keys.update(orderId),
    onSuccess: async (data: WindowOrder) => {
      const positions = syncPositionCaches(queryClient, data);
      queryClient.setQueryData<WindowOrder>(keys.getOne(orderId), (oldData) => {
        return {
          ...oldData,
          ...data,
          positions,
        };
      });
      await queryClient.invalidateQueries({ queryKey: keys.getAllPrefix });
      setHash(hashOrder(data));
      revalidator.revalidate();
    },
  });
};

export const useUpdateDeliveryAddress = () => {
  const queryClient = useQueryClient();
  const revalidator = useRevalidator();
  const { orderId } = useParams();

  if (!orderId) {
    throw new Error('orderId must be in the URL to update an order');
  }

  return useMutation({
    mutationFn: ({ address }: { address: WindowAddress }) =>
      windowApi.updateOrderDeliveryAddress(orderId, address),
    mutationKey: keys.updateDeliveryAddress(orderId),
    onSuccess: async (data: WindowOrder) => {
      queryClient.setQueryData<WindowOrder>(keys.getOne(orderId), data);
      await queryClient.invalidateQueries({ queryKey: keys.getAllPrefix });
      revalidator.revalidate();
    },
  });
};

export const useSubmitOrder = () => {
  const queryClient = useQueryClient();
  const revalidator = useRevalidator();
  const { init } = useWindowStore();

  return useMutation({
    mutationFn: ({
      orderId,
      base64Images,
    }: {
      orderId: string;
      base64Images: Record<Position['id'], string>;
    }) => windowApi.submitOrder(orderId, base64Images),
    mutationKey: keys.submit,
    onSuccess: async (data: WindowOrder, { orderId }) => {
      queryClient.setQueryData<WindowOrder>(keys.getOne(orderId), data);
      queryClient.setQueriesData<PaginatedData<WindowOrderSummary>>(
        { queryKey: keys.getAllPrefix, exact: false },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            rows: oldData.rows.map((order) =>
              order.id === orderId ? data : order
            ),
          };
        }
      );
      init(data);
      revalidator.revalidate();
    },
  });
};

export const usePlaceOrder = () => {
  const queryClient = useQueryClient();
  const revalidator = useRevalidator();
  const { init } = useWindowStore();

  return useMutation({
    mutationFn: (orderId: string) => windowApi.placeOrder(orderId),
    mutationKey: keys.placeOrder,
    onSuccess: async (data: WindowOrder, orderId: string) => {
      queryClient.setQueryData<WindowOrder>(keys.getOne(orderId), data);
      queryClient.setQueriesData<PaginatedData<WindowOrderSummary>>(
        { queryKey: keys.getAllPrefix, exact: false },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            rows: oldData.rows.map((order) =>
              order.id === orderId ? data : order
            ),
          };
        }
      );
      init(data);
      revalidator.revalidate();
    },
  });
};
